import * as React from 'react';
import { useEffect, useRef, useState } from 'react';
import _ from 'lodash';
import useFetch from 'use-http';
import { FaLock, FaUnlock } from 'react-icons/fa';
import Dialog from 'src/components/common/Dialog';
import Button from 'src/components/controls/forms/redesign/FormButton';
import RHFInputBox from 'src/components/controls/tempRHFControls/RHFInputBox';
import { useForm } from 'react-hook-form';
import TimeLockItem from 'src/models/personnel/TimeLockItem';

interface ILockPeriodDialogProps {
    dialogName: string;
    hideDialog: () => void;
    items: Array<TimeLockItem>;
}

const formatDate = (value: Date | string): string => {
    if (!value) {
        return '';
    }
    const date = new Date(value);
    return isNaN(date.getTime()) ? '' : date.toLocaleDateString();
};

// Mirrors the Time_Lock block (Period No./Begin Date/End Date, Status: Contractor/Employee) -
// each lock cell mirrors POST-QUERY's own CLOCKED/ELOCKED coloring (red when locked, green
// otherwise), rendered as a FaLock/FaUnlock glyph instead of the original colored button, and the
// double-click toggle is ported here as a single click that immediately commits (matches
// ON-UPDATE - no separate Save button, same as the real screen's single button).
const LockPeriodDialog = (props: ILockPeriodDialogProps) => {
    const { register, getValues } = useForm();
    const { get } = useFetch();
    // Its own useFetch so the toggle's response can't be confused with the search get's.
    const { put, response: putResponse } = useFetch();
    const [items, setItems] = useState<Array<TimeLockItem>>([]);
    const [message, setMessage] = useState<string>('');

    useEffect(() => {
        setItems(props.items ?? []);
    }, [props.items]);

    const fetchItems = async (searchTerm?: string) => {
        const query = searchTerm ? `?search=${encodeURIComponent(searchTerm)}` : '';
        const result = await get(`/v1.0/Payroll/payhistory/timelock${query}`);
        if (Array.isArray(result?.items)) {
            setItems(result.items);
        }
    };

    const searchRef = useRef<(term: string) => void>();
    searchRef.current = (term: string) => fetchItems(term);
    const [debouncedSearch] = useState(() => _.debounce((term: string) => {
        searchRef.current(term);
    }, 500));

    const handleSearchChange = () => {
        const term = (getValues().periodSearch || '').replace(/-/g, '');
        if (term) {
            debouncedSearch(term);
        } else {
            debouncedSearch.cancel();
            fetchItems();
        }
    };

    // Mirrors ON-UPDATE - always writes both flags together, using whichever one wasn't just
    // clicked as-is (matches the block's own in-memory values for both).
    const handleToggle = async (item: TimeLockItem, field: 'empLock' | 'conLock') => {
        const newValue = item[field] === 'Y' ? 'N' : 'Y';
        const updated: TimeLockItem = { ...item, [field]: newValue };
        setMessage('');
        await put('/v1.0/Payroll/payhistory/timelock', {
            periodNo: updated.periodNo,
            empLock: updated.empLock,
            conLock: updated.conLock,
        });

        // Only reflect the new flags once the write has actually landed. Updating first left the
        // glyph showing a lock state the database never took, and because the next toggle sends
        // both flags from the row it was reading, that unsaved value got submitted as if it were
        // current.
        if (!putResponse.ok) {
            setMessage(`Unable to update the lock for period ${item.periodNo}. Please try again.`);
            return;
        }

        setItems((prev) => prev.map((i) => (i.periodNo === item.periodNo ? updated : i)));
    };

    const renderLockButton = (item: TimeLockItem, field: 'empLock' | 'conLock') => {
        const locked = item[field] === 'Y';
        return (
            <Button
                reactIcon={locked ? FaLock : FaUnlock}
                iconStyle={{ fill: locked ? '#c0392b' : '#27ae60', width: '2rem', height: '2rem', verticalAlign: 'middle' }}
                iconHoverColor="blue"
                title={locked ? 'Locked' : 'Unlocked'}
                style={{ height: '3rem', background: 'transparent' }}
                onClick={() => handleToggle(item, field)}
            />
        );
    };

    return (
        <Dialog title="Lock Period" dialogName={props.dialogName} isExitButtonInvisible={true} bodyStyle={{ width: '40%' }}>
            <div className="form-row" style={{ marginBottom: '.5rem' }}>
                <RHFInputBox
                    name="periodSearch"
                    type="text"
                    placeholder="Search Period No."
                    register={register}
                    onChange={handleSearchChange}
                    style={{ width: '100%' }}
                    bodyStyle={{ width: '14rem' }}
                />
            </div>
            <div style={{ overflowY: 'auto', maxHeight: '24rem' }}>
                <table style={{ width: '100%' }}>
                    <thead>
                        <tr>
                            <th colSpan={3} />
                            <th colSpan={2} style={{ textAlign: 'center' }}>Status</th>
                        </tr>
                        <tr>
                            <th>Period No.</th>
                            <th>Begin Date</th>
                            <th>End Date</th>
                            <th>Contractor</th>
                            <th>Employee</th>
                        </tr>
                    </thead>
                    <tbody>
                        {items.map((item) => (
                            <tr key={item.periodNo} style={{ textAlign: 'center' }}>
                                <td>{item.periodNo}</td>
                                <td>{formatDate(item.begDate)}</td>
                                <td>{formatDate(item.endDate)}</td>
                                <td>{renderLockButton(item, 'conLock')}</td>
                                <td>{renderLockButton(item, 'empLock')}</td>
                            </tr>
                        ))}
                        {items.length === 0 && (
                            <tr>
                                <td colSpan={5} className="bold-centered">No Periods Found</td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>
            <div style={{ marginTop: '1rem', minHeight: '1.2rem', textAlign: 'center', color: 'red' }}>{message}</div>
            <div className="form-row" style={{ marginTop: '1rem', justifyContent: 'center' }}>
                <Button className="blue-hover-button" style={{ width: 'auto' }} text="Close" onClick={() => props.hideDialog()} />
            </div>
        </Dialog>
    );
};

export default LockPeriodDialog;



// WEBPACK FOOTER //
// ./src/components/personnel/dialogs/employeePayroll/LockPeriodDialog.tsx